import cronParser from "cron-parser";
import { cronParserExpression, crontabScheduleSchema } from "@agentdash/shared";

const MACROS: Record<string, string> = {
  "@yearly": "Once a year (Jan 1, 00:00)",
  "@annually": "Once a year (Jan 1, 00:00)",
  "@monthly": "Once a month (1st, 00:00)",
  "@weekly": "Once a week (Sun, 00:00)",
  "@daily": "Once a day (00:00)",
  "@midnight": "Once a day (00:00)",
  "@hourly": "Once an hour (:00)",
  "@reboot": "At every host reboot",
};

/**
 * Best-effort human summary of a cron schedule. Doesn't try to be exhaustive
 * — covers the common shapes (every N min/hour, daily at HH:MM, Mon–Fri at
 * HH:MM, weekday at HH:MM, plus @-macros). Falls back to the raw expression;
 * notably `M H D * *` (monthly on day D) has no branch and renders raw.
 */
export function describeCron(schedule: string): string {
  const macro = MACROS[schedule];
  if (macro) return macro;

  const parts = schedule.trim().split(/\s+/);
  if (parts.length !== 5) return schedule;
  const [m, h, dom, mon, dow] = parts as [string, string, string, string, string];

  // Every N minutes
  if (m.startsWith("*/") && h === "*" && dom === "*" && mon === "*" && dow === "*") {
    const n = m.slice(2);
    return `Every ${n} ${n === "1" ? "minute" : "minutes"}`;
  }
  // Hourly at minute M
  if (/^\d+$/.test(m) && h === "*" && dom === "*" && mon === "*" && dow === "*") {
    return `Every hour at :${m.padStart(2, "0")}`;
  }
  // Every N hours
  if (m === "0" && h.startsWith("*/") && dom === "*" && mon === "*" && dow === "*") {
    const n = h.slice(2);
    return `Every ${n} ${n === "1" ? "hour" : "hours"}`;
  }
  // Daily at HH:MM
  if (/^\d+$/.test(m) && /^\d+$/.test(h) && dom === "*" && mon === "*" && dow === "*") {
    return `Daily at ${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  }
  // Weekdays (Mon–Fri) at HH:MM — the ScheduleEditor's "Weekdays" preset emits `1-5`
  if (/^\d+$/.test(m) && /^\d+$/.test(h) && dom === "*" && mon === "*" && dow === "1-5") {
    return `Mon–Fri at ${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  }
  // Weekly at HH:MM on DOW
  if (/^\d+$/.test(m) && /^\d+$/.test(h) && dom === "*" && mon === "*" && /^[0-6]$/.test(dow)) {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return `${days[Number(dow)]} at ${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  }
  return schedule;
}

/**
 * Validate a schedule and return the next few fire times. `@reboot` is valid
 * but has no scheduled fire, so it comes back `ok` with an empty `times` and a
 * `note` for the caller to render in the list's place — the editor must accept
 * it, not show it as a parse error.
 */
export function previewNextFires(
  schedule: string,
  count = 5,
  tz?: string,
): { ok: true; times: Date[]; note?: string } | { ok: false; error: string } {
  // Same gate the API applies: cron-parser would happily preview a six-field
  // expression the crontab can't hold, and the save would then 400.
  const shape = crontabScheduleSchema.safeParse(schedule);
  if (!shape.success) return { ok: false, error: shape.error.issues[0]!.message };
  const expression = cronParserExpression(schedule);
  if (expression === null) return { ok: true, times: [], note: MACROS["@reboot"] };
  try {
    const it = cronParser.parseExpression(expression, {
      tz: tz ?? "UTC",
    });
    const times: Date[] = [];
    for (let i = 0; i < count; i++) {
      times.push(it.next().toDate());
    }
    return { ok: true, times };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

/**
 * Next fire time for a schedule, or null if it doesn't have one we can compute
 * (parse error, or `@reboot` which only fires at host boot). Pass the host's
 * IANA TZ when known (the host-agent reports it on each snapshot) so the
 * countdown matches what cron will actually do; falls back to the browser's
 * TZ when not.
 */
export function nextFireDate(schedule: string, tz?: string | null): Date | null {
  const expression = cronParserExpression(schedule);
  if (expression === null) return null;
  try {
    const it = cronParser.parseExpression(expression, {
      tz: tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    return it.next().toDate();
  } catch {
    return null;
  }
}
